import React, { useCallback, useMemo } from 'react';
import { Box, List, Theme, Typography, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../../assets/img/Stefanini_globe_blue white.webp';
import mobileLogo from '../../assets/img/Stefanini_logo_white.webp';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import { NavigationItem } from './navigationItem';
import { RouterElement, ROUTER_ELEMENTS } from '../../routing';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectProfile } from '../../store/profileSlice';
import { LogoutButton } from './logoutButton';

const style = {
  navbar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px 8px 16px',
  },
  icon: {
    color: ColorPalette.WHITE,
    cursor: 'pointer',
  },

  listNavigation: {
    p: 0,
    color: ColorPalette.WHITE,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  navigationItemsBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    overflow: 'auto',
    px: 0.5,
  },
  versionText: {
    fontSize: '12px !important',
    fontFamily: TypographyFamily.NEXA,
    textAlign: 'center',
    mt: 0.8,
    color: ColorPalette.WHITE,
  },

  versionTextMobile: {
    textAlign: 'left',
    pl: 1,
  },
} as const;

interface Props {
  onClose?(): void;
}

export function SidebarItems({ onClose }: Props): JSX.Element {
  const isMobile = useMediaQuery((themeMedia: Theme) =>
    themeMedia.breakpoints.down('sm')
  );

  const user = useSelector(selectProfile);

  const locationRouter = useLocation();

  const isAllowed = useCallback(
    (allowedRoles: string[] | undefined) =>
      user?.permissionGroups.some((role) => allowedRoles?.includes(role.name)),
    [user]
  );

  const filteredRoutes = useMemo(
    () =>
      ROUTER_ELEMENTS.filter((routerElement) =>
        routerElement.icon && isAllowed(routerElement.allowedRoles)
          ? routerElement
          : null
      ),
    [isAllowed]
  ) as Required<RouterElement>[];

  return (
    <Box sx={style.navbar}>
      <Box mb={2}>
        {isMobile ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <CloseIcon sx={{ ...style.icon, mr: 2.5 }} onClick={onClose} />
            <img src={mobileLogo} className="stefaniniLogo" />
          </Box>
        ) : (
          <img src={logo} className="stefaniniGlobe" />
        )}
      </Box>
      <Box sx={style.navigationItemsBox}>
        <List sx={style.listNavigation}>
          {filteredRoutes.map((routerElement) => (
            <NavigationItem
              key={routerElement.url}
              isSelected={locationRouter.pathname.includes(routerElement.url)}
              label={routerElement.label}
              url={routerElement.url}
              Icon={routerElement.icon}
            />
          ))}
        </List>

        <Box>
          <LogoutButton />

          <Typography
            sx={{
              ...style.versionText,
              ...(isMobile && style.versionTextMobile),
            }}
          >
            Version 2.0.1
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
